import * as React from 'react';
import { CreateExecutionResponseBody } from '@lct-jobs/core';
import useRestApi from 'hooks/useRestApi';
import { apiUrl } from 'utils/apiUrl';

export interface ReleaseStageUnlockExecutionResult {
  executionReference?: CreateExecutionResponseBody;
  targetTenantIds: string[];
  submitting: boolean;
  error?: Error;
  startUnlock: (tenantIds: string[]) => void;
}

/** Starts a selectively scoped UpdateTenantState release-stage execution. */
export const useReleaseStageUnlockExecution = (
  releaseStageId: string,
): ReleaseStageUnlockExecutionResult => {
  const submission = useRestApi<CreateExecutionResponseBody>(
    apiUrl('lct', `releaseStageJob/${releaseStageId}/triggerCustomBatch`),
    { manual: true },
  );
  const {
    doPost,
    loadingRequests,
    succeededRequests,
    failedRequests,
    clearSucceededRequests,
    clearFailedRequests,
  } = submission;
  const [executionReference, setExecutionReference] = React.useState<CreateExecutionResponseBody>();
  const [targetTenantIds, setTargetTenantIds] = React.useState<string[]>([]);
  const [error, setError] = React.useState<Error>();

  const startUnlock = React.useCallback(
    (tenantIds: string[]) => {
      const exactTenantIds = Array.from(new Set(tenantIds));
      if (exactTenantIds.length === 0) return;

      clearFailedRequests();
      clearSucceededRequests();
      setError(undefined);
      setExecutionReference(undefined);
      setTargetTenantIds(exactTenantIds);
      doPost({
        jobId: 'UpdateTenantState',
        jobInput: { state: 'AVAILABLE' },
        tenantList: exactTenantIds,
      });
    },
    [clearFailedRequests, clearSucceededRequests, doPost],
  );

  React.useEffect(() => {
    const succeeded = succeededRequests.at(-1)?.response?.data as
      | CreateExecutionResponseBody
      | undefined;
    if (!succeeded) return;

    setExecutionReference(succeeded);
    clearSucceededRequests();
  }, [clearSucceededRequests, succeededRequests]);

  React.useEffect(() => {
    const failed = failedRequests.at(-1)?.error;
    if (!failed) return;

    setError(failed);
    clearFailedRequests();
  }, [clearFailedRequests, failedRequests]);

  return {
    executionReference,
    targetTenantIds,
    submitting: loadingRequests,
    error,
    startUnlock,
  };
};
